import { ref, watch, onMounted, getCurrentInstance } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

export function useFormUpselling(emit, localForm) {
    const instance = getCurrentInstance()
    const toast = instance?.proxy?.$toast

    function sameIdSet(selected, catalog) {
        const selectedIds = new Set(selected.map(item => item.id ?? item))
        const catalogIds = new Set(catalog.map(item => item.id ?? item))
        if (selectedIds.size !== catalogIds.size) return false
        for (const id of selectedIds) {
            if (!catalogIds.has(id)) return false
        }
        return true
    }

    const observer = ref(null)
    const loading = ref(false)
    const loadingEventos = ref(false)
    const loadingAbonados = ref(false)
    const loadingEquipos = ref(false)
    const eventosCache = ref({})
    const teatro = store.getters.getTeatro
    const listaId = store.getters.getListaId
    const promotores = ref([])
    const abonados = ref([])
    const plantillas = ref([])
    const eventos = ref([])
    const equipos = ref([])
    const formHasErrors = ref(false)
    const validationErrorMsg = ref(null)
    const validationFailedAt = ref(null)

    const opcionesRetraso = [
        { label: 'Inmediato', value: 'inmediato' },
        { label: '2H', value: '120' },
        { label: '24H', value: '1440' },
        { label: '48H', value: '2880' },
        { label: 'Personalizar', value: 'personalizar' }
    ]

    async function fetchPromotores() {
        if (promotores.value.length) return
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.teatro.compania-eventos.index'),
                { teatro_id: teatro.id, filters: {} }
            )
            promotores.value = res.data.data.map(compania => ({ id: compania.id, label: compania.nombre }))
        } catch (error) {
            console.error(error)
        }
    }

    async function fetchPlantillas() {
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.plantillas-whatsapp.index'),
                { teatro_id: teatro.id, filters: { status: 1 } }
            )
            plantillas.value = res.data.data.map(p => ({ id: p.id, label: p.name, components: p.components }))
        } catch (error) {
            console.error(error)
        }
    }

    async function fetchAbonados() {
        if (abonados.value.length) return
        loadingAbonados.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.tipos.abonos'),
                { filters: { to_select: true } }
            )
            abonados.value = res.data.tipos_abonado.map(a => ({ id: a.id, label: a.descripcion }))
        } catch (error) {
            console.error(error)
        } finally {
            loadingAbonados.value = false
        }
    }

    async function fetchEventos() {
        const companyId = localForm.value.promotor?.id
        if (!companyId) return
        if (eventosCache.value[companyId]) {
            eventos.value = eventosCache.value[companyId]
            return
        }
        loadingEventos.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.teatro.compania-eventos.eventos'),
                { teatro_id: teatro.id, company_id: companyId, filters: { actuales: 1 } }
            )
            const data = res.data.data ?? res.data ?? []
            eventos.value = data
            eventosCache.value[companyId] = data
        } catch (error) {
            console.error(error)
        } finally {
            loadingEventos.value = false
        }
    }

    async function fetchEquipos() {
        if (!listaId) return
        if (equipos.value.length) return
        loadingEquipos.value = true
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.equipos.index'),
                { lista_id: listaId, filters: {} }
            )
            equipos.value = res.data.data ?? res.data ?? []
        } catch (error) {
            console.error(error)
        } finally {
            loadingEquipos.value = false
        }
    }

    function toggleCanal(canal) {
        const canalesArray = localForm.value.canales || []
        const index = canalesArray.indexOf(canal)
        if (index > -1) {
            canalesArray.splice(index, 1)
        } else {
            canalesArray.push(canal)
        }
        localForm.value.canales = [...canalesArray]
        
        // Reset errors when a channel is toggled
        if (observer.value) {
            observer.value.reset()
        }
    }

    async function submit() {
        // Validate that at least one channel is selected
        if (!localForm.value.canales || localForm.value.canales.length === 0) {
            formHasErrors.value = true
            validationErrorMsg.value = 'Debes seleccionar al menos un canal de comunicación'
            toast?.error?.(validationErrorMsg.value)
            return
        }

        const isValid = await observer.value.validate()
        if (!isValid) {
            formHasErrors.value = true
            validationFailedAt.value = Date.now()
            return
        } else {
            formHasErrors.value = false
        }

        loading.value = true
        try {
            const esInmediato = localForm.value.retraso === 'inmediato'
            const canalesArray = localForm.value.canales || []

            let tipo = (localForm.value.segmentacion || '');
            if (tipo === 'deportistas') {
                tipo = 'bases';
            }
            
            const isWhatsappActive = canalesArray.includes('whatsapp');
            const isEmailActive = canalesArray.includes('email');

            const payload = {
                teatro_company_id: localForm.value.promotor?.id,
                nombre: localForm.value.nombre,
                tipo_campana: tipo,
                envio_inmediato: esInmediato,
                retraso_envio: esInmediato ? null : getRetrasoEnMinutos(),
                tipo_whatsapps: isWhatsappActive,
                tipo_emails: isEmailActive,
                activa: localForm.value.activa,
                coste_envio_whatsapp: teatro.coste_envio_whatsapp_marketing,
                producto_comprado: localForm.value.restriccionProductos,
                plaza_parking_comprada: localForm.value.restriccionParking,
                seguro_comprado: localForm.value.restriccionSeguros,
            }

            if (isWhatsappActive) {
                payload.whatsapp_template_id = localForm.value.plantilla?.id;
            }

            if (isEmailActive) {
                payload.asunto_email = localForm.value.email_subject;
                payload.contenido_html = localForm.value.email_content;
            }
            if (localForm.value.segmentacion === 'eventos') {
                const validEventosIds = new Set(eventos.value.map(e => e.id))
                payload.eventos_ids = localForm.value.eventos.filter(e => validEventosIds.has(e.id)).map(e => e.id)
            }
            if (localForm.value.segmentacion === 'abonos') {
                payload.tipo_abonos_ids = localForm.value.abonados.map(a => a.id)
            }
            if (localForm.value.segmentacion === 'deportistas') {
                payload.equipos_ids = localForm.value.equipos.map(e => e.id)
            }

            // Use UPDATE if editing, CREATE if new
            const esEdicion = !!localForm.value.id
            const action = esEdicion
                ? config.getVuexAlias('back.upselling.editar')
                : config.getVuexAlias('back.upselling.crear')
            
            const params = esEdicion
                ? { campana_id: localForm.value.id, ...payload }
                : payload

            await store.dispatch(action, params)
            emit('terminado')
        } catch (error) {
            console.error(error)
        } finally {
            loading.value = false
        }
    }

    watch(() => [localForm.value.segmentacion, localForm.value.promotor?.id], ([segmentacion, companyId]) => {
        if (segmentacion === 'eventos' && companyId) {
            localForm.value.eventos = []
            localForm.value.enviarATodosEventos = false
            fetchEventos()
        } else {
            eventos.value = []
            if (segmentacion === 'eventos' && !companyId) {
                localForm.value.eventos = []
                localForm.value.segmentacion = ''
            }
        }
    })

    watch(() => localForm.value.enviarATodosAbonos, enviarATodosAbonos => {
        localForm.value.abonados = enviarATodosAbonos ? [...abonados.value] : []
    })

    watch(() => localForm.value.enviarATodosEquipos, enviarATodosEquipos => {
        localForm.value.equipos = enviarATodosEquipos ? [...equipos.value] : []
    })

    watch(() => localForm.value.enviarATodosEventos, enviarATodosEventos => {
        if (enviarATodosEventos) {
            localForm.value.eventos = [...(eventos.value ?? [])]
        } else {
            localForm.value.eventos = []
        }
    })
    watch(eventos, eventosList => {
        if (localForm.value.enviarATodosEventos) {
            localForm.value.eventos = [...(eventosList ?? [])]
        }
    })

    // Rehidratación del flag "enviar a todos" cuando el catálogo termina de
    // cargar. buildInitialForm no tiene acceso al catálogo (vive aquí y es
    // async), así que derivamos el flag comparando la selección guardada
    // contra el catálogo recién cargado. Sólo activamos el flag, nunca lo
    // apagamos automáticamente para respetar cambios explícitos del usuario.
    watch(abonados, abonadosList => {
        if (localForm.value.enviarATodosAbonos) return
        const selected = localForm.value.abonados ?? []
        if (abonadosList.length > 0 && sameIdSet(selected, abonadosList)) {
            localForm.value.enviarATodosAbonos = true
        }
    })

    watch(equipos, equiposList => {
        if (localForm.value.enviarATodosEquipos) return
        const selected = localForm.value.equipos ?? []
        if (equiposList.length > 0 && sameIdSet(selected, equiposList)) {
            localForm.value.enviarATodosEquipos = true
        }
    })

    watch(eventos, eventosList => {
        if (localForm.value.enviarATodosEventos) return
        const selected = localForm.value.eventos ?? []
        if (eventosList.length > 0 && sameIdSet(selected, eventosList)) {
            localForm.value.enviarATodosEventos = true
        }
    })

    watch(localForm, () => {
        formHasErrors.value = false
    }, { deep: true })

    function getRetrasoEnMinutos() {
        const retraso = localForm.value.retraso
        if (retraso === 'inmediato') return null
        if (retraso === 'personalizar') {
            const valor = localForm.value.retrasoPersonalizado
            const unidad = localForm.value.retrasoUnidad
            if (unidad === 'horas') return valor * 60
            return valor
        }
        const num = parseInt(retraso, 10)
        return Number.isNaN(num) ? null : num
    }
    
    onMounted(async() => {
        loading.value = true
        try {
            await Promise.all([
                fetchPromotores(),
                fetchPlantillas()
            ])

            if (localForm.value.plantilla && localForm.value.plantilla.id) {
                const match = plantillas.value.find(
                    p => Number(p.id) === Number(localForm.value.plantilla.id)
                );
                if (match) {
                    localForm.value.plantilla = match;
                }
            }

            const segmentacion = (localForm.value.segmentacion || '').toString().toLowerCase();
            const companyId = localForm.value.promotor?.id;
            if (segmentacion === 'abonos') {
                await fetchAbonados();
            }
            if (segmentacion === 'deportistas') {
                await fetchEquipos();
            }
            if (segmentacion === 'eventos' && companyId) {
                await fetchEventos();
            }
        } catch (error) {
            console.error(error)
        } finally {
            loading.value = false
        }
    })

    return {
        observer,
        loading,
        loadingEventos,
        loadingAbonados,
        loadingEquipos,
        teatro,
        eventos,
        localForm,
        promotores,
        abonados,
        plantillas,
        equipos,
        opcionesRetraso,
        submit,
        validationErrorMsg,
        validationFailedAt,
        formHasErrors,
        fetchAbonados,
        fetchEquipos,
        toggleCanal
    }
}
